import { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem("sendtoo_token"));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      fetchMe();
    } else {
      setLoading(false);
    }
  }, []);

  const fetchMe = async () => {
    try {
      const res = await axios.get(`${API}/auth/me`);
      setUser(res.data);
    } catch {
      logout();
    } finally {
      setLoading(false);
    }
  };

  const applySession = (t, u) => {
    localStorage.setItem("sendtoo_token", t);
    axios.defaults.headers.common["Authorization"] = `Bearer ${t}`;
    setToken(t);
    setUser(u);
    return u;
  };

  const login = async (identifier, password) => {
    const res = await axios.post(`${API}/auth/login`, { identifier, password });

    if (res.data.must_change_password) {
      const err = new Error("Password reset required before first login.");
      err.mustChangePassword = true;
      err.resetToken = res.data.reset_token;
      throw err;
    }

    if (res.data.requires_2fa) {
      const err = new Error("2FA verification required.");
      err.requires2FA = true;
      err.pendingToken = res.data.pending_token;
      throw err;
    }

    const { token: t, user: u } = res.data;
    return applySession(t, u);
  };

  const completePasswordReset = async (resetToken, newPassword, confirmPassword) => {
    const res = await axios.post(
      `${API}/auth/change-password`,
      { new_password: newPassword, confirm_password: confirmPassword },
      { headers: { Authorization: `Bearer ${resetToken}` } }
    );
    const { token: t, user: u } = res.data;
    return applySession(t, u);
  };

  const completeTwoFactorLogin = async (pendingToken, code) => {
    const res = await axios.post(`${API}/auth/login/verify-2fa`, { pending_token: pendingToken, code });
    const { token: t, user: u } = res.data;
    return applySession(t, u);
  };

  const sendForgotPasswordOtp = async (identifier) => {
    return axios.post(`${API}/auth/forgot-password/send`, { identifier });
  };

  const completeForgotPasswordReset = async (identifier, otp, newPassword, confirmPassword) => {
    const res = await axios.post(`${API}/auth/forgot-password/reset`, {
      identifier, otp, new_password: newPassword, confirm_password: confirmPassword
    });
    const { token: t, user: u } = res.data;
    return applySession(t, u);
  };

  const sendLoginOtp = async (phone) => {
    return axios.post(`${API}/auth/otp/send`, { phone });
  };

  const verifyLoginOtp = async (phone, otp) => {
    const res = await axios.post(`${API}/auth/otp/verify`, { phone, otp });
    const { token: t, user: u } = res.data;
    return applySession(t, u);
  };

  const register = async (name, email, phone, password) => {
    const res = await axios.post(`${API}/auth/register`, { name, email, phone, password });
    const { token: t, user: u } = res.data;
    localStorage.setItem("sendtoo_token", t);
    axios.defaults.headers.common["Authorization"] = `Bearer ${t}`;
    setToken(t);
    setUser(u);
    return u;
  };

  const logout = () => {
    localStorage.removeItem("sendtoo_token");
    delete axios.defaults.headers.common["Authorization"];
    setToken(null);
    setUser(null);
  };

  const refreshUser = async () => {
    await fetchMe();
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, logout, refreshUser, completePasswordReset, completeTwoFactorLogin, sendForgotPasswordOtp, completeForgotPasswordReset, sendLoginOtp, verifyLoginOtp }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};
